import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import {
  formatHours,
  formatOperationalDays,
  formatFullAddress,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  AnimatedNumber,
  MotionList,
  MotionItem,
} from './themeHelpers';

// Zen palette — soft earth and sage
const ZEN_BG = '#F7F5F2';
const ZEN_PAPER = '#EDE9E3';
const ZEN_SAGE = '#7A9E7E';
const ZEN_CLAY = '#C5937A';
const ZEN_DARK = '#2C2C27';
const ZEN_MUTED = '#7A7A72';
const ZEN_BORDER = '#DDD8CF';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

// Muted tint of accentColor for backgrounds (mix with white at low alpha)
function accentTint(accentColor, alpha = 0.08) {
  return `${accentColor}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
}

function ZenTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const galleryImages = images.slice(0, 6);

  const defaultDescription = `${library.name} is a place of stillness. Designed with nature's patience in mind — ample light, clean surfaces, and the gentle hum of focused minds. Come, breathe, study.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: ZEN_BG, color: ZEN_DARK }}>

      {/* ── Hero — serene, photography first ── */}
      <Box sx={{ position: 'relative', height: { xs: '55vh', md: '78vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{
            width: '100%', height: '100%', objectFit: 'cover',
            filter: 'saturate(0.8) brightness(0.88)',
          }}
        />
        {/* Soft vignette */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(44,44,39,0.1) 40%, rgba(44,44,39,0.65) 100%)',
        }} />

        {/* Minimal arrows — just chevrons, soft */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              aria-label="Previous image"
              sx={{
                position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.7)', color: ZEN_DARK,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                width: 44, height: 44,
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <IconButton
              onClick={nextImage}
              aria-label="Next image"
              sx={{
                position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.7)', color: ZEN_DARK,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                width: 44, height: 44,
              }}
            >
              <ArrowForward fontSize="small" />
            </IconButton>
          </>
        )}

        {/* Dots — subtle line indicators */}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: idx === currentImageIndex ? 28 : 8, height: 2,
                  bgcolor: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.45)',
                  borderRadius: 1, cursor: 'pointer', transition: 'all 0.5s ease',
                }}
              />
            ))}
          </Box>
        )}

        {/* Hero text — restrained */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 6 } }}>
          <Container maxWidth="lg">
            <Reveal y={16} duration={0.8}>
              {/* Thin rule above name */}
              <Box sx={{ width: 40, height: 1, bgcolor: 'rgba(255,255,255,0.6)', mb: 2 }} />
              <Typography
                sx={{
                  fontFamily: '"Georgia", "Times New Roman", serif',
                  fontWeight: 400,
                  fontStyle: 'italic',
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  color: 'white',
                  letterSpacing: 0.5,
                  lineHeight: 1.2,
                  mb: 1.5,
                }}
              >
                {library.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3.5, opacity: 0.7 }}>
                <LocationOn sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: 'white' }}>{library.address}</Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => onBookNow()}
                sx={{
                  borderColor: 'rgba(255,255,255,0.7)', color: 'white',
                  px: 4, py: 1.25, borderRadius: 8,
                  letterSpacing: 1, fontWeight: 400,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'white' },
                }}
              >
                Book a Seat
              </Button>
            </Reveal>
          </Container>
        </Box>
      </Box>

      {/* ── Thin accent rule ── */}
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${ZEN_BG}, ${accentColor}88, ${ZEN_BG})` }} />

      {/* ── About ── */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 14 }, textAlign: 'center' }}>
        <Reveal duration={0.9}>
          <Typography
            variant="overline"
            sx={{ color: ZEN_SAGE, letterSpacing: 4, fontWeight: 600, display: 'block', mb: 2 }}
          >
            About
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Georgia", serif',
              fontSize: { xs: '1.3rem', md: '1.65rem' },
              fontWeight: 400,
              lineHeight: 1.75,
              color: ZEN_DARK,
              fontStyle: 'italic',
            }}
          >
            {library.description || defaultDescription}
          </Typography>
        </Reveal>
      </Container>

      {/* ── Quick facts — organic horizontal band ── */}
      <Box sx={{ bgcolor: ZEN_PAPER, borderTop: `1px solid ${ZEN_BORDER}`, borderBottom: `1px solid ${ZEN_BORDER}`, py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {seatDisplay && (
              <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                <Reveal>
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700,
                      color: accentColor,
                      lineHeight: 1,
                    }}
                  >
                    <AnimatedNumber value={seatDisplay} />
                  </Typography>
                  <Typography variant="body2" sx={{ color: ZEN_MUTED, mt: 0.5, letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Seats Available
                  </Typography>
                </Reveal>
              </Grid>
            )}

            <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
              <Reveal delay={0.1}>
                <AccessTime sx={{ color: ZEN_SAGE, fontSize: 32, mb: 1 }} />
                <Typography variant="body1" fontWeight={600} sx={{ color: ZEN_DARK }}>
                  {hoursStr || 'Contact for hours'}
                </Typography>
                {daysStr && (
                  <Typography variant="body2" sx={{ color: ZEN_MUTED }}>{daysStr}</Typography>
                )}
              </Reveal>
            </Grid>

            {library.locker_count > 0 && (
              <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                <Reveal delay={0.2}>
                  <LockOutlined sx={{ color: ZEN_CLAY, fontSize: 32, mb: 1 }} />
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: { xs: '2rem', md: '3rem' },
                      fontWeight: 700,
                      color: accentColor,
                      lineHeight: 1,
                    }}
                  >
                    <AnimatedNumber value={library.locker_count} />
                  </Typography>
                  <Typography variant="body2" sx={{ color: ZEN_MUTED, mt: 0.5, letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Lockers
                  </Typography>
                </Reveal>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* ── Plans ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
        <Reveal>
          <Typography variant="overline" sx={{ color: ZEN_SAGE, letterSpacing: 4, fontWeight: 600, display: 'block', textAlign: 'center', mb: 1 }}>
            Membership
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontFamily: '"Georgia", serif', fontWeight: 400, textAlign: 'center', color: ZEN_DARK, mb: 6 }}
          >
            Choose your rhythm
          </Typography>
        </Reveal>

        {plans.length > 0 ? (
          <Grid container spacing={3} justifyContent="center">
            {plans.map((plan) => {
              const planName = plan.name || titleCasePlanType(plan.plan_type);
              const price = formatPrice(plan.price_cents);
              const period = planPeriodLabel(plan.plan_type);
              const discPct = plan.first_time_discount;
              const discPrice = discountedFirstPrice(plan.price_cents, discPct);

              return (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: ZEN_PAPER,
                      border: `1px solid ${ZEN_BORDER}`,
                      borderRadius: 3,
                      height: '100%',
                      position: 'relative',
                      transition: 'transform 0.5s ease, box-shadow 0.5s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${accentTint(accentColor, 0.18)}`,
                        borderColor: `${accentColor}55`,
                      },
                    }}
                  >
                    {discPct > 0 && (
                      <Chip
                        label={`${discPct}% OFF first time`}
                        size="small"
                        sx={{
                          position: 'absolute', top: 12, right: 12,
                          bgcolor: accentTint(accentColor, 0.15),
                          color: accentColor,
                          fontWeight: 600, fontSize: '0.65rem',
                          border: `1px solid ${accentColor}44`,
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography
                        variant="overline"
                        sx={{ color: ZEN_MUTED, letterSpacing: 2, display: 'block', mb: 2 }}
                      >
                        {planName}
                      </Typography>

                      {/* Thin decorative line */}
                      <Box sx={{ width: 24, height: 1, bgcolor: ZEN_BORDER, mx: 'auto', mb: 2 }} />

                      {price && (
                        <>
                          {discPct > 0 && discPrice !== null ? (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ color: ZEN_MUTED, textDecoration: 'line-through' }}>{price}</Typography>
                              <Typography sx={{ fontFamily: '"Georgia", serif', fontSize: '2rem', fontWeight: 700, color: accentColor }}>
                                {formatPrice(discPrice)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: ZEN_MUTED }}>first time, then {price}</Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontFamily: '"Georgia", serif', fontSize: '2.5rem', fontWeight: 700, color: ZEN_DARK, mb: 0.5 }}>
                              {price}
                            </Typography>
                          )}
                          {period && (
                            <Typography variant="caption" sx={{ color: ZEN_MUTED, display: 'block', mb: 2 }}>{period}</Typography>
                          )}
                        </>
                      )}

                      <Box sx={{ textAlign: 'left', mb: 2 }}>
                        {plan.duration_days && (
                          <PlanFeature icon={<CalendarToday />} color={ZEN_MUTED}>
                            Valid {plan.duration_days} days
                          </PlanFeature>
                        )}
                        <PlanFeature icon={<Schedule />} color={ZEN_MUTED}>
                          {hoursLabel(plan.hours_per_day)}
                        </PlanFeature>
                        {plan.recurring && (
                          <PlanFeature icon={<Repeat />} color={ZEN_SAGE}>
                            Auto-renews
                          </PlanFeature>
                        )}
                      </Box>

                      {plan.description ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: ZEN_MUTED, display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2,
                          }}
                        >
                          {plan.description}
                        </Typography>
                      ) : null}

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => onBookNow(plan)}
                        sx={{
                          borderColor: ZEN_BORDER, color: ZEN_DARK,
                          borderRadius: 8,
                          '&:hover': { borderColor: accentColor, color: accentColor, bgcolor: accentTint(accentColor, 0.06) },
                          fontWeight: 400, letterSpacing: 0.5,
                        }}
                      >
                        Select
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ bgcolor: ZEN_PAPER, border: `1px solid ${ZEN_BORDER}`, borderRadius: 3, p: 5, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: ZEN_DARK, mb: 2 }}>Reach out for membership details</Typography>
            <Button
              variant="outlined"
              onClick={() => onBookNow()}
              sx={{ borderColor: ZEN_BORDER, color: ZEN_DARK, borderRadius: 8, '&:hover': { borderColor: accentColor, color: accentColor } }}
            >
              Get in Touch
            </Button>
          </Box>
        )}
      </Container>

      {/* ── Gallery — breathing room ── */}
      <Box sx={{ bgcolor: ZEN_PAPER, borderTop: `1px solid ${ZEN_BORDER}`, py: { xs: 8, md: 14 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="overline" sx={{ color: ZEN_SAGE, letterSpacing: 4, fontWeight: 600, display: 'block', textAlign: 'center', mb: 1 }}>
              Gallery
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 400, textAlign: 'center', color: ZEN_DARK, mb: 6 }}>
              Spaces that breathe
            </Typography>
          </Reveal>
          <Grid container spacing={2}>
            {galleryImages.map((img, idx) => (
              <Grid item xs={6} md={4} key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  onClick={() => openLightbox(idx)}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: { xs: 160, md: 220 },
                    objectFit: 'cover',
                    borderRadius: 2,
                    cursor: 'pointer',
                    filter: 'saturate(0.7)',
                    transition: 'filter 0.5s ease, transform 0.5s ease',
                    '&:hover': { filter: 'saturate(1)', transform: 'scale(1.02)' },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Amenities ── */}
      {library.amenities?.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
          <Reveal>
            <Typography variant="overline" sx={{ color: ZEN_SAGE, letterSpacing: 4, fontWeight: 600, display: 'block', textAlign: 'center', mb: 1 }}>
              Facilities
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 400, textAlign: 'center', color: ZEN_DARK, mb: 6 }}>
              Thoughtfully provided
            </Typography>
          </Reveal>
          <MotionList
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}
          >
            {library.amenities.map((amenity, idx) => (
              <MotionItem key={idx}>
                <Box
                  sx={{
                    textAlign: 'center', py: 3, px: 2,
                    border: `1px solid ${ZEN_BORDER}`,
                    borderRadius: 3,
                    bgcolor: ZEN_BG,
                    transition: 'border-color 0.4s ease',
                    '&:hover': { borderColor: `${accentColor}66` },
                  }}
                >
                  <Box sx={{ color: ZEN_SAGE, mb: 1.5 }}>
                    {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 28 } })}
                  </Box>
                  <Typography variant="body2" sx={{ color: ZEN_DARK, fontSize: '0.8rem' }}>{amenity}</Typography>
                </Box>
              </MotionItem>
            ))}
          </MotionList>
        </Container>
      )}

      {/* ── Location ── */}
      <Box sx={{ bgcolor: ZEN_PAPER, borderTop: `1px solid ${ZEN_BORDER}`, py: { xs: 8, md: 14 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="overline" sx={{ color: ZEN_SAGE, letterSpacing: 4, fontWeight: 600, display: 'block', textAlign: 'center', mb: 1 }}>
              Location
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 400, textAlign: 'center', color: ZEN_DARK, mb: 6 }}>
              Find your stillness
            </Typography>
          </Reveal>
          <Grid container spacing={5}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 320, borderRadius: 3, overflow: 'hidden', border: `1px solid ${ZEN_BORDER}` }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Library Location"
                    width="100%" height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ height: '100%', bgcolor: ZEN_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: ZEN_MUTED }}>Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, pt: 1 }}>
                {fullAddress && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocationOn sx={{ color: ZEN_SAGE, mt: 0.3, flexShrink: 0, fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: ZEN_DARK, mb: 0.25 }}>Address</Typography>
                      <Typography variant="body2" sx={{ color: ZEN_MUTED }}>{fullAddress}</Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Phone sx={{ color: ZEN_SAGE, mt: 0.3, flexShrink: 0, fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: ZEN_DARK, mb: 0.25 }}>Phone</Typography>
                      <Typography variant="body2" sx={{ color: ZEN_MUTED }}>
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Email sx={{ color: ZEN_SAGE, mt: 0.3, flexShrink: 0, fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: ZEN_DARK, mb: 0.25 }}>Email</Typography>
                      <Typography variant="body2" sx={{ color: ZEN_MUTED }}>
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── CTA — vast whitespace, minimal ── */}
      <Box sx={{ py: { xs: 12, md: 18 }, textAlign: 'center', bgcolor: ZEN_BG }}>
        <Container maxWidth="sm">
          <Reveal duration={1}>
            {/* Accent brush stroke (decorative blob via border-radius) */}
            <Box sx={{
              width: 80, height: 4, borderRadius: 4,
              background: `linear-gradient(90deg, ${ZEN_SAGE}, ${accentColor})`,
              mx: 'auto', mb: 4,
            }} />
            <Typography
              sx={{
                fontFamily: '"Georgia", serif',
                fontWeight: 400,
                fontSize: { xs: '1.6rem', md: '2.4rem' },
                color: ZEN_DARK,
                lineHeight: 1.45,
                mb: 2.5,
              }}
            >
              A quieter place to become who you want to be
            </Typography>
            <Typography sx={{ color: ZEN_MUTED, mb: 5, lineHeight: 1.8 }}>
              Reserve your seat at {library.name} today.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                bgcolor: accentColor, px: 5, py: 1.5,
                borderRadius: 8, fontWeight: 400, letterSpacing: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: accentColor, filter: 'brightness(0.92)', boxShadow: 'none' },
              }}
            >
              Book a Seat
            </Button>
          </Reveal>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ py: 4, borderTop: `1px solid ${ZEN_BORDER}` }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: ZEN_MUTED, fontFamily: '"Georgia", serif' }}>{library.name}</Typography>
            <Typography variant="caption" sx={{ color: ZEN_MUTED }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: ZEN_BG } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          aria-label="Close gallery"
          sx={{ position: 'absolute', right: 8, top: 8, color: ZEN_MUTED, zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={galleryImages[lightboxIdx]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default ZenTheme;
